/**
 * SalaryBreakdownTable — Tableau détaillé du mois en cours par employé.
 * Vue compact PRO : une ligne par employé, colonnes Brut·CNSS·ITS·Avances·Retenues·Bons·NET.
 */
import React from "react";
import { AlertCircle, ShieldCheck, Smartphone, Copy } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => (Math.round(v || 0)).toLocaleString("fr-FR");

const copyToClipboard = (text, label) => {
  if (!text) return;
  const t = String(text).replace(/\s+/g, "");
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(t).then(
      () => toast.success(`${label || "Copié"} : ${t}`),
      () => toast.error("Copie impossible"),
    );
  }
};

const SalaryBreakdownTable = ({ breakdown = [], period = "" }) => {
  if (!breakdown || breakdown.length === 0) return null;

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/40 overflow-hidden" data-testid="salary-breakdown-table">
      <div className="px-3 py-2 border-b border-slate-700 flex items-center justify-between">
        <p className="text-slate-300 font-bold text-[12.5px]">
          Détail par employé — <span className="text-slate-500 text-[11px]">{period}</span>
        </p>
        <span className="text-slate-500 text-[10.5px]">{breakdown.length} employé{breakdown.length > 1 ? "s" : ""}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="bg-slate-900/60 text-slate-300 text-[10px] uppercase tracking-wider">
              <th className="text-left px-3 py-2 font-bold">Employé</th>
              <th className="text-right px-2 py-2 font-bold text-blue-300">Brut</th>
              <th className="text-right px-2 py-2 font-bold hidden md:table-cell text-purple-300">CNSS</th>
              <th className="text-right px-2 py-2 font-bold hidden md:table-cell text-amber-300">ITS</th>
              <th className="text-right px-2 py-2 font-bold hidden sm:table-cell">Avances</th>
              <th className="text-right px-2 py-2 font-bold hidden sm:table-cell">Retenues</th>
              <th className="text-right px-2 py-2 font-bold text-violet-300">Bons</th>
              <th className="text-right px-3 py-2 font-bold text-emerald-300">NET</th>
            </tr>
          </thead>
          <tbody>
            {breakdown.map((r) => {
              const bonsTotal = (r.credits_auto || 0) + (r.credits_manual || 0);
              const retenuesTotal = (r.manual_deductions || 0);
              return (
                <tr key={r.id} className="border-t border-slate-700/50 hover:bg-slate-700/20" data-testid={`breakdown-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-white font-semibold text-[12.5px]">{r.name}</span>
                      {r.signed && <ShieldCheck className="w-3 h-3 text-emerald-400" title="Bulletin signé" />}
                      {!r.has_payslip && <AlertCircle className="w-3 h-3 text-slate-500" title="Aucun bulletin généré" />}
                    </div>
                    {r.position && <p className="text-slate-500 text-[10px]">{r.position}</p>}
                    {r.mobile_money ? (
                      <button
                        type="button"
                        onClick={() => copyToClipboard(r.mobile_money, `${r.name.split(" ")[0]} · MoMo`)}
                        className="mt-0.5 inline-flex items-center gap-1 text-[10.5px] font-mono text-amber-300 hover:text-amber-200 group"
                        title="Cliquer pour copier le numéro Mobile Money"
                        data-testid={`breakdown-momo-${r.id}`}
                      >
                        <Smartphone className="w-3 h-3" />
                        <span className="font-bold">{r.mobile_money}</span>
                        <Copy className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ) : (
                      <p className="mt-0.5 text-[10px] text-slate-600 italic">Pas de N° Mobile Money</p>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right font-mono font-bold text-blue-200">{fmt(r.brut)}</td>
                  <td className="px-2 py-2 text-right font-mono text-purple-300 hidden md:table-cell">
                    {r.cnss_emp > 0 ? fmt(r.cnss_emp) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-amber-300 hidden md:table-cell">
                    {r.its > 0 ? fmt(r.its) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-slate-300 hidden sm:table-cell">
                    {r.advances > 0 ? fmt(r.advances) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-slate-300 hidden sm:table-cell">
                    {retenuesTotal > 0 ? fmt(retenuesTotal) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-violet-200">
                    {bonsTotal > 0 ? (
                      <>
                        {fmt(bonsTotal)}
                        {r.credits_auto > 0 && r.credits_manual > 0 && (
                          <div className="text-[9px] text-slate-500">A {fmt(r.credits_auto)} · M {fmt(r.credits_manual)}</div>
                        )}
                      </>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-black text-emerald-300 text-[13px]">{fmt(r.net)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-600 bg-slate-900/40 font-bold">
              <td className="px-3 py-2 text-slate-300 uppercase text-[10.5px] tracking-wider">TOTAL</td>
              <td className="px-2 py-2 text-right font-mono text-blue-200 text-[12.5px]">
                {fmt(breakdown.reduce((s, r) => s + (r.brut || 0), 0))}
              </td>
              <td className="px-2 py-2 text-right font-mono text-purple-300 hidden md:table-cell">
                {fmt(breakdown.reduce((s, r) => s + (r.cnss_emp || 0), 0))}
              </td>
              <td className="px-2 py-2 text-right font-mono text-amber-300 hidden md:table-cell">
                {fmt(breakdown.reduce((s, r) => s + (r.its || 0), 0))}
              </td>
              <td className="px-2 py-2 text-right font-mono text-slate-300 hidden sm:table-cell">
                {fmt(breakdown.reduce((s, r) => s + (r.advances || 0), 0))}
              </td>
              <td className="px-2 py-2 text-right font-mono text-slate-300 hidden sm:table-cell">
                {fmt(breakdown.reduce((s, r) => s + (r.manual_deductions || 0), 0))}
              </td>
              <td className="px-2 py-2 text-right font-mono text-violet-200">
                {fmt(breakdown.reduce((s, r) => s + (r.credits_auto || 0) + (r.credits_manual || 0), 0))}
              </td>
              <td className="px-3 py-2 text-right font-mono font-black text-emerald-300 text-[13px]">
                {fmt(breakdown.reduce((s, r) => s + (r.net || 0), 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default SalaryBreakdownTable;
